import React from "react";

import { makeStyles } from "@mui/styles";

import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";

import { useConfirm } from "material-ui-confirm";
import { useDispatch } from "react-redux";
import {
  removeDishFromCart,
  updateDishTakeaway,
  increaseDishQty,
} from "store/actions/order";
import { showErrorSnackbar } from "store/actions/snackbarMessage";
import {
  getQuantityChangeAction,
  shouldDisableDecreaseButton,
  getMinimumQuantity,
} from "util/cartQuantityLogic";

const useStyles = makeStyles((theme) => ({
  card: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1.5, 2),
    gap: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  qtyBadge: {
    minWidth: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 14,
    flexShrink: 0,
    marginTop: 2,
  },
  details: {
    flex: 1,
    minWidth: 0,
  },
  dishName: {
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.35,
    color: theme.palette.text.primary,
  },
  optionText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: 2,
  },
  rightCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  price: {
    fontSize: 15,
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 20,
    overflow: "hidden",
  },
  controlBtn: {
    width: 34,
    height: 34,
    borderRadius: 0,
    padding: 0,
    color: theme.palette.text.secondary,
    "&:disabled": {
      color: theme.palette.action.disabled,
    },
  },
  qtyDisplay: {
    minWidth: 24,
    textAlign: "center",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: "34px",
    userSelect: "none",
  },
  takeawayRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
}));

const getOptionString = (orderItem) => {
  let descList = [];
  if (orderItem.size) {
    descList.push(orderItem.size.name.split("#")[0].trim());
  }

  const optionDescList = orderItem.options.map((option) => {
    let quantityStr = "";
    if (option.quantity > 1) {
      quantityStr = " " + option.quantity + "x";
    }
    return option.name + quantityStr;
  });

  if (optionDescList.length > 0) {
    descList.push(...optionDescList);
  }

  if (
    orderItem.specialInstructions &&
    orderItem.specialInstructions.trim() !== ""
  ) {
    descList.push(orderItem.specialInstructions);
  }

  if (descList.length > 0) {
    return descList.join(", ");
  } else {
    return "";
  }
};

const CartItem = (props) => {
  const { item, canDishesTakeaway, currencySymbol } = props;
  const classes = useStyles();

  const dispatch = useDispatch();
  const updateQuantityDish = (itemId, change) =>
    dispatch(increaseDishQty({ id: itemId, change }));
  const removeDish = (itemId) => dispatch(removeDishFromCart(itemId));
  const updateTakeaway = (id, value) =>
    dispatch(updateDishTakeaway(id, value));
  const showError = (msg) => dispatch(showErrorSnackbar(msg));
  const confirm = useConfirm();

  const updateQuantity = (change) => {
    const id = item.id;
    const minQuantity = getMinimumQuantity(item.dish);
    const action = getQuantityChangeAction(item.quantity, change, minQuantity);

    if (action.type === "DELETE") {
      confirm({ description: action.message })
        .then(() => {
          removeDish(id);
        })
        .catch(() => {});
    } else if (action.type === "ERROR") {
      showError(action.message);
    } else if (action.type === "UPDATE") {
      updateQuantityDish(id, change);
    }
  };

  const getItemPrice = () => {
    return (item.subTotal * item.quantity).toFixed(2).toString();
  };

  const handleSwitchChange = (event) => {
    if (event.target.name === "isForTakeaway") {
      updateTakeaway(item.id, event.target.checked);
    }
  };

  const isDecreaseDelete =
    item.quantity <= 1 ||
    (item.dish.minQuantity && item.quantity <= item.dish.minQuantity);
  const optStr = getOptionString(item);
  const altText = item.dish.altName || item.dish.description;

  return (
    <Box className={classes.card}>
      <div className={classes.qtyBadge}>{item.quantity}</div>

      <div className={classes.details}>
        <Typography className={classes.dishName}>
          {item.dish.name}
        </Typography>
        {altText && (
          <Typography className={classes.optionText}>{altText}</Typography>
        )}
        {optStr && (
          <Typography className={classes.optionText}>{optStr}</Typography>
        )}
        {item.dish?.minQuantity > 1 && (
          <Chip
            label={`Min: ${item.dish.minQuantity}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 0.5, height: 20, fontSize: 11 }}
          />
        )}
        {canDishesTakeaway === true &&
          item.dish.isTakeawayDisabled !== true && (
            <div className={classes.takeawayRow}>
              <Switch
                size="small"
                checked={item.isTakeaway}
                onChange={handleSwitchChange}
                name="isForTakeaway"
                color="primary"
              />
              <Typography variant="caption" color="textSecondary">
                Takeaway
              </Typography>
            </div>
          )}
        {item.isTakeaway === true && (
          <Chip
            label="Takeaway"
            size="small"
            color="primary"
            sx={{ mt: 0.5, height: 20, fontSize: 11 }}
          />
        )}
      </div>

      <div className={classes.rightCol}>
        <Typography className={classes.price}>
          {currencySymbol}
          {getItemPrice()}
        </Typography>
        <div className={classes.controls}>
          <IconButton
            aria-label="decrease quantity"
            className={classes.controlBtn}
            onClick={() => updateQuantity(-1)}
            disabled={shouldDisableDecreaseButton(
              item.quantity,
              getMinimumQuantity(item.dish)
            )}
            size="small"
          >
            {isDecreaseDelete ? (
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            ) : (
              <RemoveIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
          <span className={classes.qtyDisplay}>{item.quantity}</span>
          <IconButton
            aria-label="increase quantity"
            className={classes.controlBtn}
            onClick={() => updateQuantity(1)}
            size="small"
            color="primary"
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>
    </Box>
  );
};

export default CartItem;
